/**
 * What can be established about this register's rules, and what cannot.
 *
 * The second half is not a shorter first half, and the layout says so by giving it its own
 * section rather than a footnote. Much of the corpus commences on the matter's earliest
 * admissible date rather than on a researched one, and most registers have no opening date; a
 * chain beginning at the floor cannot have a hole, so "no holes found" would be a fact about the
 * floor and not about the law. A reader has to be able to tell a register that is sound from one
 * nobody can judge.
 *
 * There is no score here, and the absence is deliberate. Completeness is measured against a
 * reference set and none exists for this subject, so a percentage would be a confident number
 * with nothing behind it. Findings are counted within a tier and the tiers are never summed,
 * because adding quantities that are not comparable invents a scale.
 */

import { Link } from 'react-router';
import { routes } from '~/lib/routes';
import { SectionCard } from '~/ui/Surface';
import { EmptyState } from '~/ui/Feedback';
import { Pill } from '~/ui/Pill';
import type { Tone } from '~/ui/tone';
import { plural } from '~/lib/format';
import { useGaps } from '~/api/queries';
import { useMatterView } from './useMatterView';
import type { GapFinding, GapTier } from '~/api/schema';
import styles from './Matter.module.css';

const TIER_LABEL: Record<GapTier, string> = {
  cannot_be_worked: 'Cannot be worked',
  holds_rules_it_cannot_answer_with: 'Holds rules it cannot answer with',
  answers_without_evidence: 'Answers without evidence',
};

const TIER_NOTE: Record<GapTier, string> = {
  cannot_be_worked:
    'Research cannot run here, or nothing is held. Everything subtler is premature until these are resolved.',
  holds_rules_it_cannot_answer_with:
    'Something is held and does not work. A filing can reach these rules and get the wrong answer, or none.',
  answers_without_evidence:
    'These rules answer, and rest on nothing anybody can read back to a source.',
};

const TIER_TONE: Record<GapTier, Tone> = {
  cannot_be_worked: 'issue',
  holds_rules_it_cannot_answer_with: 'open',
  answers_without_evidence: 'quest',
};

const ORDER: readonly GapTier[] = [
  'cannot_be_worked',
  'holds_rules_it_cannot_answer_with',
  'answers_without_evidence',
];

function FindingRow({ finding }: { finding: GapFinding }) {
  return (
    <li className={styles.gapFinding}>
      <p className={styles.gapDetail}>
        {finding.lane ? <strong>{finding.lane}. </strong> : null}
        {finding.detail}
      </p>
      <p className={styles.gapAction}>{finding.action}</p>
    </li>
  );
}

export function GapsTab() {
  const { code, matter } = useMatterView();
  const gaps = useGaps(code, matter);

  if (gaps.isPending) return <EmptyState title="Reading">Working out what can be established.</EmptyState>;
  if (!gaps.data) return null;
  const data = gaps.data;

  // Absent from an older payload rather than empty, and the two mean the same thing here.
  const attested = data.attested ?? [];
  const observations = data.observations ?? [];
  const inFlight = (data.laneStates ?? []).filter((one) => one.state === 'proposed');

  const byTier = ORDER.map((tier) => ({
    tier,
    findings: data.findings.filter((one) => one.tier === tier),
  })).filter((group) => group.findings.length > 0);

  return (
    <>
      <SectionCard
        title="What this is"
        note={
          <>
            Two lists, and the second is not a shorter first. A finding says something is wrong; a
            check that could not run says nobody can tell. There is no score, because completeness
            is measured against a set of rules that ought to exist, and no such set exists for this
            subject.
          </>
        }
      >
        <p className={styles.gapSummary}>
          Computed against {data.computedAt} over {plural(data.rulesExamined, 'live rule')}.{' '}
          {data.tier ? (
            <>
              The most blocking tier is <strong>{TIER_LABEL[data.tier].toLowerCase()}</strong>, with{' '}
              {plural(data.openItems, 'open item')} in it.
            </>
          ) : data.saysNothing ? (
            <strong>Nothing could be established about this register, which is not the same as nothing being wrong.</strong>
          ) : (
            <>No findings.</>
          )}
        </p>
        {data.corpusWide ? <p className={styles.gapCorpus}>{data.corpusWide}</p> : null}
      </SectionCard>

      {data.answerability ? (
        <SectionCard
          title="What a filing actually gets back"
          note={
            <>
              The denominator is the rules this register <em>holds</em>, never the rules that
              exist. A register holding one rule answers one of one — this says whether what is
              held works, not whether it is all there is.
            </>
          }
        >
          <p className={styles.gapSummary}>
            For a filing on {data.answerability.filing}
            {data.answerability.clamped
              ? ' — moved into the register\u2019s admissible window, so this concerns a different date from today'
              : ''}
            , the engine computed{' '}
            <strong>
              {data.answerability.computed} of {data.answerability.attempted}
            </strong>
            .{' '}
            {data.answerability.needsOfficeDate > 0 ? (
              <>
                {plural(data.answerability.needsOfficeDate, 'other')} run from an office&rsquo;s own
                correspondence and are counted apart rather than called unanswerable.{' '}
              </>
            ) : null}
            {data.answerability.missingInput > 0 ? (
              <>
                {plural(data.answerability.missingInput, 'refusal')} came from a date the probe did
                not supply, which measures the probe and not the register.
              </>
            ) : null}
          </p>
        </SectionCard>
      ) : null}

      {data.research ? (
        <SectionCard
          title="What has been attempted"
          note="Taken from the most recent run, with repeated questions removed. Summing them across runs would rank the register that got the most attention as the worst one."
        >
          <p className={styles.gapSummary}>
            {data.research.state === 'never_researched'
              ? 'No research run has ever covered this register and matter.'
              : data.research.state === 'researched_without_result'
                ? 'Researched, and the most recent run left questions it could not settle.'
                : 'Researched, and the most recent run settled everything it raised.'}
            {data.research.declined > 0 ? (
              <>
                {' '}
                {plural(data.research.declined, 'proposal')} here{' '}
                {data.research.declined === 1 ? 'was' : 'were'} turned down by a person. Those are
                answered questions, not open gaps, so they are not counted as findings.
              </>
            ) : null}
          </p>
        </SectionCard>
      ) : null}

      {observations.length > 0 ? (
        <SectionCard
          title="Questions the corpus raises"
          note="Not findings, and kept apart from them: the corpus is one client's export, so 'most registers hold this' is a fact about that file and not about this register's law. Answering one is recording that this register has no such rule — which is what stops it being asked again."
        >
          <ul className={styles.gapList}>
            {observations.map((one) => (
              <li key={one.lane} className={styles.gapFinding}>
                <p className={styles.gapDetail}>
                  <strong>{one.lane}.</strong> Held by {one.heldBy} of the {one.ofTotal} registers
                  that hold anything for this matter. This one does not, and nobody has recorded
                  that its law has none.
                </p>
                <p className={styles.gapAction}>
                  Record that this register's law has no such rule, or research it.
                </p>
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {inFlight.length > 0 ? (
        <SectionCard
          title="Already proposed, awaiting review"
          note="A run has proposed these and nobody has reviewed it yet. They are not asked about above, because a question whose answer is already in flight is noise rather than a question."
        >
          <ul className={styles.gapList}>
            {inFlight.map((one) => (
              <li key={one.lane} className={styles.gapFinding}>
                <p className={styles.gapDetail}>
                  <strong>{one.lane}.</strong> Proposed by a research run that has not been
                  reviewed.
                </p>
                {one.runId ? (
                  <p className={styles.gapAction}>
                    <Link to={routes.run(one.runId)}>Review the run that proposes it</Link>
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {data.worthResearching ? (
        <SectionCard
          title="Worth researching"
          note="Taken from the commencement dates the whole corpus shares, and said once here rather than against each register — a check of this kind fires on more than half the corpus, so per register it would find nothing."
        >
          <p className={styles.gapSummary}>{data.worthResearching}</p>
        </SectionCard>
      ) : null}

      {data.authority ? (
        <SectionCard
          title="What the treaty organisation publishes"
          note="The only comparison here that can tell a wrong value from a missing one. A disagreement is something to investigate — nothing is ever written to a rule from it."
        >
          <p className={styles.gapSummary}>
            {data.authority.table}, covering {plural(data.authority.offices, 'office')}
            {data.authority.retrievedOn ? <>, read on {data.authority.retrievedOn}</> : null}.{' '}
            {data.authority.comparable ? (
              <>A comparison was possible here.</>
            ) : (
              <strong>{data.authority.note}</strong>
            )}
          </p>
        </SectionCard>
      ) : null}

      {byTier.map(({ tier, findings }) => (
        <SectionCard
          key={tier}
          title={TIER_LABEL[tier]}
          note={TIER_NOTE[tier]}
          actions={
            <div className={styles.gapCount}>
              <Pill tone={TIER_TONE[tier]}>{plural(findings.length, 'finding')}</Pill>
            </div>
          }
        >
          <ul className={styles.gapList}>
            {findings.map((finding, index) => (
              <FindingRow key={`${finding.kind}-${finding.ruleId ?? ''}-${index}`} finding={finding} />
            ))}
          </ul>
        </SectionCard>
      ))}

      {attested.length > 0 ? (
        <SectionCard
          title="Absences somebody has recorded"
          note="A person has said this register's law holds no rule of these kinds. Dated rather than permanent, because the law changes — and kept visible, because an absence nobody can see is indistinguishable from nobody having looked."
        >
          <ul className={styles.gapList}>
            {attested.map((one) => (
              <li key={one.id} className={styles.gapFinding}>
                <p className={styles.gapDetail}>
                  <strong>{one.lane}.</strong> {one.reason}
                </p>
                <p className={styles.gapAction}>
                  No such rule from {one.absentFrom}
                  {one.absentTo ? <> until {one.absentTo}</> : <> onwards</>}. Recorded by{' '}
                  {one.attestedBy}.{' '}
                  {one.stale ? <strong>Nobody has re-checked this since.</strong> : null}
                </p>
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {data.notEstablished.length > 0 ? (
        <SectionCard
          title="What could not be established"
          note="These checks did not pass. They could not run, because what they read is unknown — and a check that cannot run must never be read as one that found nothing."
        >
          <ul className={styles.gapList}>
            {data.notEstablished.map((one) => (
              <li key={one.check} className={styles.gapFinding}>
                <p className={styles.gapDetail}>{one.reason}</p>
                <p className={styles.gapAction}>
                  Not established for {plural(one.rules, 'rule')}.
                </p>
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {data.findings.length === 0 && data.notEstablished.length === 0 ? (
        <EmptyState title="Nothing found">
          Every check this engine can make ran, and none of them found anything. That is a statement
          about the checks, not a guarantee about the law.
        </EmptyState>
      ) : null}
    </>
  );
}
